import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { User, Mail, Shield, KeyRound, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  lider_proyecto: 'Líder de Proyecto',
  levantador: 'Levantador',
  cliente: 'Cliente',
};

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800',
  lider_proyecto: 'bg-blue-100 text-blue-800',
  levantador: 'bg-green-100 text-green-800',
  cliente: 'bg-orange-100 text-orange-800',
};

export default function UserProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: '', newPass: '', confirm: '' });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPass !== form.confirm) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }
    if (form.newPass.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    setSaving(true);
    try {
      await apiClient.patch('/auth/change-password', {
        user_id: user.id,
        user_email: user.email,
        current_password: form.current,
        new_password: form.newPass,
      });
      setSuccess(true);
      setForm({ current: '', newPass: '', confirm: '' });
      toast.success('Contraseña actualizada correctamente');
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al cambiar contraseña');
    } finally { setSaving(false); }
  };

  const backPath = user?.role === 'cliente' ? '/cliente' : '/dashboard';

  return (
    <div className="min-h-screen bg-soem-bg">
      {/* Header */}
      <div className="bg-soem-navy px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate(backPath)} className="text-slate-400 hover:text-white text-sm flex items-center gap-2">
          ← Volver
        </button>
        <span className="text-white/30">|</span>
        <span className="text-white font-semibold text-sm">Mi Perfil</span>
      </div>

      <div className="max-w-2xl mx-auto p-8 space-y-6">
        <h1 className="text-2xl font-bold text-soem-navy">Mi Perfil</h1>

        {/* User info card */}
        <div className="soem-card p-6">
          <h2 className="font-bold text-soem-navy mb-4 flex items-center gap-2">
            <User size={18} className="text-soem-primary" /> Información de la cuenta
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-soem-primary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-2xl font-black">{user?.name?.charAt(0)}</span>
              </div>
              <div>
                <p className="font-bold text-xl text-soem-navy">{user?.name}</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${ROLE_COLORS[user?.role]}`}>
                  {ROLE_LABELS[user?.role]}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div>
                <label htmlFor="f-correo-electrónico" className="soem-label block mb-1">Correo electrónico</label>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-slate-600 text-sm">
                  <Mail size={14} className="text-slate-400" />
                  <span>{user?.email}</span>
                </div>
              </div>
              <div>
                <label htmlFor="f-rol-en-el-sistema" className="soem-label block mb-1">Rol en el sistema</label>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-slate-600 text-sm">
                  <Shield size={14} className="text-slate-400" />
                  <span>{ROLE_LABELS[user?.role]}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Change password */}
        <div className="soem-card p-6">
          <h2 className="font-bold text-soem-navy mb-4 flex items-center gap-2">
            <KeyRound size={18} className="text-soem-primary" /> Cambiar contraseña
          </h2>

          {success && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-sm px-4 py-3 mb-4 text-green-800 text-sm">
              <CheckCircle size={16} />
              <span>Contraseña actualizada correctamente.</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {[
              { key: 'current', label: 'Contraseña actual', placeholder: '••••••••', showKey: 'current' },
              { key: 'newPass', label: 'Nueva contraseña', placeholder: 'Mínimo 8 caracteres', showKey: 'new' },
              { key: 'confirm', label: 'Confirmar nueva contraseña', placeholder: 'Repetir nueva contraseña', showKey: 'confirm' },
            ].map(({ key, label, placeholder, showKey }) => (
              <div key={key}>
                <label htmlFor={`f-pwd-${key}`} className="soem-label block mb-1.5">{label}</label>
                <div className="relative">
                  <input
id={`f-pwd-${key}`}                     type={show[showKey] ? 'text' : 'password'}
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    placeholder={placeholder}
                    required
                    className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                    data-testid={`password-${key}`}
                  />
                  <button type="button"
                    onClick={() => setShow((s) => ({ ...s, [showKey]: !s[showKey] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {show[showKey] ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
            ))}

            {form.newPass && form.confirm && form.newPass !== form.confirm && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle size={14} />
                <span>Las contraseñas nuevas no coinciden</span>
              </div>
            )}
            {form.newPass && form.newPass.length < 8 && form.newPass.length > 0 && (
              <div className="flex items-center gap-2 text-orange-600 text-sm">
                <AlertCircle size={14} />
                <span>La contraseña debe tener al menos 8 caracteres</span>
              </div>
            )}

            <button type="submit" disabled={saving || !form.current || !form.newPass || !form.confirm}
              className="soem-btn-primary px-6 py-2.5 text-sm disabled:opacity-60 flex items-center gap-2"
              data-testid="change-password-btn">
              {saving ? (
                <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Actualizando...</>
              ) : (
                <><KeyRound size={14} /> Actualizar contraseña</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
